import {batchActions} from "redux-batch-enhancer";
import {formatRoute} from 'react-router-named-routes';

import * as notificationAction
  from "../../../shared/modules/notification/actions/notificationsAction";
import {
  FILE_CONNECT_URL,
  FILE_DELETE_URL,
  FILE_GET_RIGHTS_URL,
  FILE_UPLOAD_URL
} from "../constants/api";
import {
  IMAGE_ACTIVE,
  IMAGE_CLEAN,
  IMAGE_CONNECT,
  IMAGE_DELETED,
  IMAGE_DISCONNECT,
  IMAGE_GET,
  IMAGE_RELOAD,
  IMAGE_RIGHTS_UPDATED,
  IMAGE_UPLOAD
} from "../constants/actionTypes";
import relativesImage from '../../../../assets/img/relatives_211x211.png';
import {
  NOTIFICATION_ERROR,
  NOTIFICATION_SUCCESS
} from "../../../shared/modules/notification/constants";
import {b64EncodeUnicode, getStorageImageUrl} from '../../../shared/actions';
import {requestAuth} from "../../../request";

/**
 * Shows image
 * @param {string} hash - image hash
 * @param {string} url - image url
 * @param {string} title - image title
 * @param {number} rights - image rights
 * @param {array} seniors - list of seniors that are connected with an images
 * @param {array} comments - senior comments to an image
 * @returns {function(dispatch): Promise<any>} promised thunk
 */
export function showImageAction(hash, url, title, rights, seniors, comments) {
  return (dispatch) => {
    return Promise.resolve(
      dispatch(
        {
          type: IMAGE_ACTIVE,
          payload: {
            url: url,
            hash: hash,
            title: title,
            rights: rights,
            seniors: seniors,
            comments: comments
          }
        }
      )
    );
  };
}

/**
 * Gets image delete action
 * @param {string} hash - image hash
 * @returns {{
 *    type: string,
 *    payload: {hash: string}
 * }} delete action
 */
export function deleteImageAction(hash) {
  return {
    type: IMAGE_DELETED,
    payload: {hash: hash}
  };
}

/**
 * Gets image clean action
 * @param {string} url - image url
 * @returns {{
 *    type: string,
 *    payload: {url: string}
 * }} clean action
 */
export function cleanImageAction(url) {
  return {
    type: IMAGE_CLEAN,
    payload: {url: url}
  };
}

/**
 * Uploads an image
 * @param {ImageUploadState} data - image data
 * @param {object} user - current user
 * @param {array} recipients - list of recipients
 * @returns {function(dispatch): Promise<any>} promised thunk
 */
export function imageUpload(data, user, recipients) {
  // TODO (kkashpur) ORLY? iOS Patch:
  if (data.imageText.length === 0) data.imageText = " ";

  return (dispatch) => {
    return new Promise((resolve, reject) => {
      requestAuth()
        .post(FILE_UPLOAD_URL)
        .set("p_prof_id", recipients.reduce((accumulate, currentValue) => {
            accumulate.push(currentValue.prof_id);
            return accumulate;
          }, [])
        )
        .attach(b64EncodeUnicode(data.imageText), data.file)
        .field("seniors", recipients.reduce((accumulate, currentValue) => {
            accumulate.push(currentValue.prof_id);
            return accumulate;
          }, [])
        )
        .on("error", () => {
          dispatch(
            notificationAction.addNotification(
              __("Uploading the image failed."),
              NOTIFICATION_ERROR
            )
          )
        })
        .then((res) => {
          for (let i = 0; i < res.body.length; i++) {
            dispatch(
              {
                type: IMAGE_RELOAD,
                url: data.imagePreviewUrl,
                payload: {
                  url_prev: getStorageImageUrl(res.body[i].cf_prev_url),
                  url: res.body[i].cf_url
                }
              }
            )
          }
          resolve(res);
        })
        .catch((error) => {
          dispatch(cleanImageAction(data.imagePreviewUrl));
          reject(error);
        });

      for (let i = 0; i < recipients.length; i++) {
        dispatch(
          {
            type: IMAGE_UPLOAD,
            payload: {
              image: {
                url: data.imagePreviewUrl
              },
              sender: {
                id: user.cust_id,
                name: `${user.firstname} ${user.lastname}`,
                image: relativesImage
              },
              recipient: {
                id: recipients[i].prof_id,
                name: `${recipients[i].firstname} ${recipients[i].lastname}`,
                image: recipients[i].imgUrl
              },
              title: data.imageText
            }
          }
        )
      }
    });
  };
}

/**
 * Deletes image
 * @param {string} hash - image hash
 * @returns {function(dispatch): Promise<any>} promised thunk
 */
export function deleteImage(hash) {
  return (dispatch) => {
    return requestAuth()
        .del(FILE_DELETE_URL)
        .send({ cf_url: hash })
        .on("error", () => {
          dispatch(
            notificationAction.addNotification(
              __("The image deletion has failed."),
              NOTIFICATION_ERROR
            )
          )
        })
        .ok((res) => {
          return res.noContent;
        })
        .then(() => dispatch(
          batchActions(
            [
              deleteImageAction(hash),
              notificationAction.addNotification(
                __("The picture has been deleted"),
                NOTIFICATION_SUCCESS
              )
            ]
          )
        ));
  };
}

/**
 * Gets image rights
 * @param {string} hash - image hash
 * @returns {function(dispatch): Promise<any>} promised thunk
 */
export function getRights(hash) {
  return (dispatch) => {
      const RIGHTS = 'rights';

    return requestAuth()
        .post(FILE_GET_RIGHTS_URL)
        .send([{ hash: hash }])
        .ok((res) => {
          return res.ok && RIGHTS in res.body[0]
        })
        .then((res) => dispatch(
          {
            type: IMAGE_RIGHTS_UPDATED,
            payload: res.body[0][RIGHTS]
          }
        ));
  };
}

/**
 * Adds a connection between an image and senior
 * @param {string} hash - image hash
 * @param {string} prof_id - senior profile identification
 * @returns {function(dispatch): Promise<any>} promised thunk
 */
export function addImageConnect(hash, prof_id) {
  return (dispatch) => {
    return requestAuth()
        .post(formatRoute(FILE_CONNECT_URL, {fileHash: hash}))
        .send({prof_id: prof_id})
        .then(() => dispatch(
          {
            type: IMAGE_CONNECT,
            senior: prof_id
          }
        ));
  };
}

/**
 * Removes a connection between an image and senior
 * @param {string} hash - image hash
 * @param {string} prof_id - senior profile identification
 * @returns {function(dispatch): Promise<any>} promised thunk
 */
export function removeImageConnect(hash, prof_id) {
  return (dispatch) => {
    return requestAuth()
      .del(formatRoute(FILE_CONNECT_URL, {fileHash: hash}))
      .send({prof_id: prof_id})
      .then(() => dispatch(
        {
          type: IMAGE_DISCONNECT,
          senior: prof_id
        }
      ));
  };
}

/**
 * Gets image data
 * todo (kashpur) implement a request to the backend to getRelatives data, we still miss title
 * @param {string} hash - image hash
 * @returns {function(dispatch): Promise<any>} promised thunk
 */
export function getImage(hash) {
  return (dispatch) => {
    return Promise.resolve(
      dispatch(
        {
          type: IMAGE_GET,
          payload: {
            url: getStorageImageUrl(hash),
            hash: hash
          }
        }
      ));
  }
}
